/**
 * Try to parse out a filename from a Content‑Disposition header.
 * Supports both RFC 5987 (UTF‑8’…) and the simpler filename="…" form.
 */
export function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  // RFC 5987: filename*=UTF-8''… (percent‑encoded)
  const rfc5987 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (rfc5987) {
    try {
      return decodeURIComponent(rfc5987[1]);
    } catch {
      // fall back to raw
      return rfc5987[1];
    }
  }

  // Simple form: filename="…"
  const simple = /filename\s*=\s*"([^"]+)"/i.exec(header);
  if (simple) {
    return simple[1];
  }

  return null;
}

/**
 * Download a Blob (or any binary data) using an <a> tag.
 * @param blob The data to download
 * @param filename The filename to save as
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Convenience: take an actual fetch-Response, pull out headers + blob,
 * pick a filename (header or fallback), and kick off the download.
 */
export async function downloadResponse(response: Response, fallbackFilename: string) {
  const header = response.headers.get("Content-Disposition");
  const filename = parseFilenameFromContentDisposition(header) || fallbackFilename;
  const blob = await response.blob();
  downloadBlob(blob, filename);
}
